/**
 * Shared cryptographic utilities for backup operations
 */

import type { KeyshareBackupData } from '../types';

export interface EncryptedBackup {
  encryptedData: string;
  iv: string;
  salt: string;
  version: string;
  encryptionMethod: 'passkey' | 'password';
  credentialId?: string;
}

export interface EnvelopeEncryptedShare {
  ciphertext_share: string;
  wrapped_dek: string;
  kdf: { prf: string; hkdf: string; info: string };
  alg: { data: string; wrap: string };
  aad: { userId: string; version: number; deviceId?: string };
  createdAt?: string;
  expiresAt?: string | null;
  encryptionMethod?: 'passkey' | 'password' | 'server-managed';
  credentialId?: string;
}

/**
 * Convert base64 string to Uint8Array
 */
export function base64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert Uint8Array to base64 string
 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert Uint8Array to hex string
 */
export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Derive encryption key from password using PBKDF2
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 150000,
      hash: 'SHA-256',
    },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Derive KEK (Key Encryption Key) from passkey using PRF extension
 */
export async function deriveKEKFromPasskey(
  userId: string,
  requiredCredentialId?: string
): Promise<{ kek: ArrayBuffer; credentialId: string }> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const allowCredentials: PublicKeyCredentialDescriptor[] | undefined = requiredCredentialId
    ? [
        {
          type: 'public-key' as const,
          id: base64ToBytes(requiredCredentialId) as BufferSource,
          transports: ['internal'] as AuthenticatorTransport[],
        },
      ]
    : undefined;

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge,
      rpId: window.location.hostname,
      userVerification: 'required',
      allowCredentials,
      extensions: {
        prf: {
          eval: {
            first: new TextEncoder().encode(`backup-kek-${userId}`),
          },
        },
      },
    },
  })) as any;

  if (!credential?.getClientExtensionResults) {
    throw new Error('Passkey authentication failed');
  }

  const prfResults = credential.getClientExtensionResults();
  if (!prfResults?.prf?.results?.first) {
    throw new Error('PRF extension not supported or failed');
  }

  const resultCredentialId = bytesToBase64(new Uint8Array(credential.rawId));

  return {
    kek: prfResults.prf.results.first,
    credentialId: resultCredentialId,
  };
}

/**
 * Derive backup password from passkey using PRF extension
 */
export async function deriveBackupPasswordFromPasskey(
  userId: string,
  credentialId?: string
): Promise<{ password: string; credentialId: string }> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));

  const allowCredentials: PublicKeyCredentialDescriptor[] | undefined = credentialId
    ? [
        {
          type: 'public-key' as const,
          id: base64ToBytes(credentialId) as BufferSource,
          transports: ['internal'] as AuthenticatorTransport[],
        },
      ]
    : undefined;

  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge,
      rpId: window.location.hostname,
      userVerification: 'required',
      allowCredentials,
      extensions: {
        prf: {
          eval: {
            first: new TextEncoder().encode(`backup-password-${userId}`),
          },
        },
      },
    },
  })) as any;

  if (!credential?.getClientExtensionResults) {
    throw new Error('Passkey authentication failed');
  }

  const prfResults = credential.getClientExtensionResults();
  if (!prfResults?.prf?.results?.first) {
    throw new Error('PRF extension not supported or failed');
  }

  const prfOutput = new Uint8Array(prfResults.prf.results.first);
  const password = bytesToBase64(prfOutput);
  const resultCredentialId = bytesToBase64(
    new Uint8Array(credential.rawId)
  );

  return { password, credentialId: resultCredentialId };
}

/**
 * Encrypt keyshare data using password or passkey
 */
export async function encryptKeyshare(
  data: KeyshareBackupData,
  password: string,
  encryptionMethod: 'passkey' | 'password',
  credentialId?: string
): Promise<any> {
  const encoder = new TextEncoder();
  const plaintext = JSON.stringify(data);
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const passwordKey = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveBits']);
  const keyMaterial = await crypto.subtle.deriveBits({ name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' }, passwordKey, 256);
  const key = await crypto.subtle.importKey('raw', keyMaterial, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoder.encode(plaintext));

  // Create checksum
  const checksumData = `${data.userId}:${data.sessionId}:${encryptionMethod}`;
  const checksumBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(checksumData));
  const checksum = bytesToBase64(new Uint8Array(checksumBuffer));

  const timestamp = Date.now();

  return {
    data: bytesToBase64(new Uint8Array(encryptedData)),
    iv: bytesToBase64(iv),
    salt: bytesToBase64(salt),
    version: '1.0',
    checksum,
    magic: 'LUMIA_BACKUP_V1',
    encryptionMethod,
    createdAt: timestamp,
    credentialId: encryptionMethod === 'passkey' ? credentialId : undefined,
  };
}

/**
 * Legacy v2 key order used before the canonical-sort fix.
 * Existing v2 backups were encrypted with this order.
 */
const LEGACY_V2_KEY_ORDER = ['userId', 'version', 'deviceId'] as const;

/**
 * Serialize AAD object with a specific key order for legacy fallback
 */
function serializeAadWithKeyOrder(
  aad: Record<string, unknown>,
  keyOrder: readonly string[]
): BufferSource {
  const ordered: Record<string, unknown> = {};
  for (const key of keyOrder) {
    if (key in aad) ordered[key] = aad[key];
  }
  return new TextEncoder().encode(JSON.stringify(ordered)) as unknown as BufferSource;
}

/**
 * Serialize AAD object to bytes for use as AES-GCM additionalData.
 * Uses canonical (alphabetically sorted) key order for determinism,
 * regardless of the input key order.
 */
function serializeAad(aad: { userId: string; version: number; deviceId?: string }): BufferSource {
  const sorted = Object.keys(aad).sort().reduce((obj, key) => {
    obj[key] = (aad as Record<string, unknown>)[key];
    return obj;
  }, {} as Record<string, unknown>);
  return new TextEncoder().encode(JSON.stringify(sorted)) as unknown as BufferSource;
}

/**
 * Encrypt keyshare using envelope encryption with passkey-derived KEK
 */
export async function envelopeEncryptKeyshare(
  data: KeyshareBackupData,
  userId: string
): Promise<EnvelopeEncryptedShare> {
  // Derive KEK from passkey and get credential ID
  const { kek: kekBytes, credentialId } = await deriveKEKFromPasskey(userId);
  console.log('[envelopeEncryptKeyshare] Using credential ID for encryption:', credentialId);

  const aad = { userId: data.userId, version: 2, deviceId: 'embarkai-ui-kit' };
  const aadBytes = serializeAad(aad);

  const dek = crypto.getRandomValues(new Uint8Array(32));
  const kek = await crypto.subtle.importKey('raw', kekBytes, 'AES-GCM', false, ['encrypt']);

  const plaintext = JSON.stringify(data);
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const dekCryptoKey = await crypto.subtle.importKey('raw', dek, 'AES-GCM', false, ['encrypt']);

  const dataIv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: dataIv, additionalData: aadBytes }, dekCryptoKey, plaintextBytes);

  const wrapIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappedDekData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: wrapIv, additionalData: aadBytes }, kek, dek);

  const ciphertextWithIv = new Uint8Array(dataIv.length + encryptedData.byteLength);
  ciphertextWithIv.set(dataIv, 0);
  ciphertextWithIv.set(new Uint8Array(encryptedData), dataIv.length);

  const wrappedDekWithIv = new Uint8Array(wrapIv.length + wrappedDekData.byteLength);
  wrappedDekWithIv.set(wrapIv, 0);
  wrappedDekWithIv.set(new Uint8Array(wrappedDekData), wrapIv.length);

  return {
    ciphertext_share: bytesToBase64(ciphertextWithIv),
    wrapped_dek: bytesToBase64(wrappedDekWithIv),
    kdf: { prf: 'webauthn-prf', hkdf: 'hkdf-sha256', info: 'client-share' },
    alg: { data: 'aes-256-gcm', wrap: 'aes-256-gcm' },
    aad,
    createdAt: new Date().toISOString(),
    expiresAt: null,
    encryptionMethod: 'passkey',
    credentialId: credentialId || undefined
  };
}

/**
 * Encrypt keyshare using envelope encryption with password-derived KEK
 */
export async function envelopeEncryptKeyshareWithPassword(
  data: KeyshareBackupData,
  password: string
): Promise<EnvelopeEncryptedShare> {
  console.log('[envelopeEncryptKeyshareWithPassword] Encrypting with password');

  const aad = { userId: data.userId, version: 2, deviceId: 'embarkai-ui-kit' };
  const aadBytes = serializeAad(aad);

  // Generate DEK and derive KEK from password
  const dek = crypto.getRandomValues(new Uint8Array(32));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const kekKey = await deriveKeyFromPassword(password, salt);

  const plaintext = JSON.stringify(data);
  const plaintextBytes = new TextEncoder().encode(plaintext);
  const dekCryptoKey = await crypto.subtle.importKey('raw', dek, 'AES-GCM', false, ['encrypt']);

  const dataIv = crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: dataIv, additionalData: aadBytes }, dekCryptoKey, plaintextBytes);

  const wrapIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappedDekData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: wrapIv, additionalData: aadBytes }, kekKey, dek);

  const ciphertextWithIv = new Uint8Array(dataIv.length + encryptedData.byteLength);
  ciphertextWithIv.set(dataIv, 0);
  ciphertextWithIv.set(new Uint8Array(encryptedData), dataIv.length);

  const wrappedDekWithIv = new Uint8Array(salt.length + wrapIv.length + wrappedDekData.byteLength);
  wrappedDekWithIv.set(salt, 0);
  wrappedDekWithIv.set(wrapIv, salt.length);
  wrappedDekWithIv.set(new Uint8Array(wrappedDekData), salt.length + wrapIv.length);

  return {
    ciphertext_share: bytesToBase64(ciphertextWithIv),
    wrapped_dek: bytesToBase64(wrappedDekWithIv),
    kdf: { prf: 'pbkdf2', hkdf: 'hkdf-sha256', info: 'client-share' },
    alg: { data: 'aes-256-gcm', wrap: 'aes-256-gcm' },
    aad,
    createdAt: new Date().toISOString(),
    expiresAt: null,
    encryptionMethod: 'password'
  };
}

/**
 * Decrypt keyshare using envelope encryption with passkey-derived KEK
 */
export async function envelopeDecryptKeyshare(
  envelope: any,
  userId: string
): Promise<KeyshareBackupData> {
  console.log('[iframe][envelopeDecryptKeyshare] Starting decryption process');
  console.log('[iframe][envelopeDecryptKeyshare] Envelope credential ID:', envelope.credentialId);

  console.log('[iframe][envelopeDecryptKeyshare] Deriving KEK from passkey...');
  const { kek: kekBytes } = await deriveKEKFromPasskey(userId, envelope.credentialId);
  console.log('[iframe][envelopeDecryptKeyshare] KEK bytes length:', kekBytes.byteLength);

  console.log('[iframe][envelopeDecryptKeyshare] Importing KEK as crypto key...');
  const kek = await crypto.subtle.importKey('raw', kekBytes, 'AES-GCM', false, ['decrypt']);

  const useAad = envelope.aad?.version >= 2;

  console.log('[iframe][envelopeDecryptKeyshare] Decrypting wrapped DEK...');
  const wrappedDekWithIv = base64ToBytes(envelope.wrapped_dek);
  console.log('[iframe][envelopeDecryptKeyshare] Wrapped DEK with IV length:', wrappedDekWithIv.length);
  const wrapIv = wrappedDekWithIv.slice(0, 12);
  const wrappedDekData = wrappedDekWithIv.slice(12);
  console.log('[iframe][envelopeDecryptKeyshare] Wrap IV length:', wrapIv.length, 'Wrapped DEK data length:', wrappedDekData.length);

  // Build AAD candidates: canonical (sorted) first, then legacy key order for pre-fix v2 backups
  const aadCandidates: (BufferSource | undefined)[] = useAad
    ? [serializeAad(envelope.aad), serializeAadWithKeyOrder(envelope.aad, LEGACY_V2_KEY_ORDER)]
    : [undefined];

  let lastError: unknown;
  for (const aadBytes of aadCandidates) {
    try {
      const dekBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: wrapIv, ...(aadBytes && { additionalData: aadBytes }) }, kek, wrappedDekData);
      console.log('[iframe][envelopeDecryptKeyshare] DEK decrypted successfully, length:', dekBytes.byteLength);

      console.log('[iframe][envelopeDecryptKeyshare] Importing DEK as crypto key...');
      const dekCryptoKey = await crypto.subtle.importKey('raw', dekBytes, 'AES-GCM', false, ['decrypt']);

      console.log('[iframe][envelopeDecryptKeyshare] Decrypting main data...');
      const ciphertextWithIv = base64ToBytes(envelope.ciphertext_share);
      console.log('[iframe][envelopeDecryptKeyshare] Ciphertext with IV length:', ciphertextWithIv.length);
      const dataIv = ciphertextWithIv.slice(0, 12);
      const encryptedData = ciphertextWithIv.slice(12);
      console.log('[iframe][envelopeDecryptKeyshare] Data IV length:', dataIv.length, 'Encrypted data length:', encryptedData.length);

      const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: dataIv, ...(aadBytes && { additionalData: aadBytes }) }, dekCryptoKey, encryptedData);
      console.log('[iframe][envelopeDecryptKeyshare] Main data decrypted successfully, length:', decryptedData.byteLength);

      console.log('[iframe][envelopeDecryptKeyshare] Parsing JSON...');
      const plaintext = new TextDecoder().decode(decryptedData);
      const result = JSON.parse(plaintext) as KeyshareBackupData;
      console.log('[iframe][envelopeDecryptKeyshare] Decryption completed successfully');
      return result;
    } catch (error) {
      lastError = error;
      if (error instanceof Error && error.name === 'OperationError' && aadCandidates.indexOf(aadBytes) < aadCandidates.length - 1) {
        console.log('[iframe][envelopeDecryptKeyshare] AAD mismatch, trying fallback key order...');
        continue;
      }
    }
  }

  console.error('[iframe][envelopeDecryptKeyshare] Decryption failed:', lastError);
  if (lastError instanceof Error && lastError.name === 'OperationError') {
    const credentialHint = envelope.credentialId
      ? `The backup requires a specific passkey (ID: ${envelope.credentialId.slice(-8)}...)`
      : 'The backup was encrypted with a different passkey than the one you selected';
    throw new Error(`Passkey mismatch: ${credentialHint}. If you have multiple passkeys, please try again and select the correct passkey that was used when creating this backup.`);
  }
  throw new Error('Failed to decrypt keyshare: ' + (lastError instanceof Error ? lastError.message : String(lastError)));
}

/**
 * Decrypt keyshare using envelope encryption with password-derived KEK
 */
export async function envelopeDecryptKeyshareWithPassword(
  envelope: any,
  password: string
): Promise<KeyshareBackupData> {
  console.log('[iframe][envelopeDecryptKeyshareWithPassword] Starting password-based decryption');

  const wrappedDekWithSaltAndIv = base64ToBytes(envelope.wrapped_dek);

  // Extract salt (16 bytes), wrapIv (12 bytes), and wrapped DEK data
  const salt = wrappedDekWithSaltAndIv.slice(0, 16);
  const wrapIv = wrappedDekWithSaltAndIv.slice(16, 28);
  const wrappedDekData = wrappedDekWithSaltAndIv.slice(28);

  const useAad = envelope.aad?.version >= 2;

  console.log('[iframe][envelopeDecryptKeyshareWithPassword] Salt length:', salt.length, 'Wrap IV length:', wrapIv.length, 'Wrapped DEK data length:', wrappedDekData.length);

  // Build AAD candidates: canonical (sorted) first, then legacy key order for pre-fix v2 backups
  const aadCandidates: (BufferSource | undefined)[] = useAad
    ? [serializeAad(envelope.aad), serializeAadWithKeyOrder(envelope.aad, LEGACY_V2_KEY_ORDER)]
    : [undefined];

  let lastError: unknown;
  for (const aadBytes of aadCandidates) {
    try {
      // Derive KEK from password using the same salt
      const kekKey = await deriveKeyFromPassword(password, salt);

      // Decrypt the DEK
      const dekBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: wrapIv, ...(aadBytes && { additionalData: aadBytes }) }, kekKey, wrappedDekData);
      console.log('[iframe][envelopeDecryptKeyshareWithPassword] DEK decrypted successfully, length:', dekBytes.byteLength);

      // Import DEK as crypto key
      const dekCryptoKey = await crypto.subtle.importKey('raw', dekBytes, 'AES-GCM', false, ['decrypt']);

      // Decrypt main data
      const ciphertextWithIv = base64ToBytes(envelope.ciphertext_share);
      const dataIv = ciphertextWithIv.slice(0, 12);
      const encryptedData = ciphertextWithIv.slice(12);

      const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: dataIv, ...(aadBytes && { additionalData: aadBytes }) }, dekCryptoKey, encryptedData);
      console.log('[iframe][envelopeDecryptKeyshareWithPassword] Main data decrypted successfully');

      const plaintext = new TextDecoder().decode(decryptedData);
      const result = JSON.parse(plaintext) as KeyshareBackupData;
      console.log('[iframe][envelopeDecryptKeyshareWithPassword] Decryption completed successfully');
      return result;
    } catch (error) {
      lastError = error;
      if (error instanceof Error && error.name === 'OperationError' && aadCandidates.indexOf(aadBytes) < aadCandidates.length - 1) {
        console.log('[iframe][envelopeDecryptKeyshareWithPassword] AAD mismatch, trying fallback key order...');
        continue;
      }
    }
  }

  console.error('[iframe][envelopeDecryptKeyshareWithPassword] Decryption failed:', lastError);
  if (lastError instanceof Error && lastError.name === 'OperationError') {
    throw new Error('Incorrect password: Unable to decrypt the backup with the provided password.');
  }
  throw new Error('Failed to decrypt keyshare with password: ' + (lastError instanceof Error ? lastError.message : String(lastError)));
}

/**
 * Decrypt keyshare from local file backup
 * Uses 100,000 PBKDF2 iterations for backward compatibility with file backups
 */
export async function decryptKeyshare(
  encryptedBackup: any,
  password: string
): Promise<KeyshareBackupData> {
  console.log('[iframe][decryptKeyshare] Starting file backup decryption');

  // Validate backup format and version
  if (encryptedBackup.magic && encryptedBackup.magic !== 'LUMIA_BACKUP_V1') {
    throw new Error('Invalid backup file format');
  }
  if (encryptedBackup.version && encryptedBackup.version !== '1.0') {
    throw new Error(`Unsupported backup version: ${encryptedBackup.version}`);
  }

  try {
    const decoder = new TextDecoder();

    // Support both old (encryptedData) and new (data) field names for backward compatibility
    const dataField = encryptedBackup.data || encryptedBackup.encryptedData;

    // Only decode base64 fields that we know are encrypted
    // Ignore additional metadata fields like userId, createdAt, etc.
    if (!encryptedBackup.salt || !encryptedBackup.iv || !dataField) {
      console.error('[iframe][decryptKeyshare] Backup structure:', Object.keys(encryptedBackup));
      throw new Error('Missing required encrypted backup fields (salt, iv, data/encryptedData)');
    }

    const salt = base64ToBytes(encryptedBackup.salt);
    const iv = base64ToBytes(encryptedBackup.iv);
    const encryptedData = base64ToBytes(dataField);

    // Derive key from password using PBKDF2 with 100,000 iterations (for file backups)
    const encoder = new TextEncoder();
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveBits']
    );

    const keyMaterial = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt as BufferSource,
        iterations: 100000, // File backups use 100,000 iterations
        hash: 'SHA-256',
      },
      passwordKey,
      256
    );

    const key = await crypto.subtle.importKey(
      'raw',
      keyMaterial,
      'AES-GCM',
      false,
      ['decrypt']
    );

    const decryptedData = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv as BufferSource },
      key,
      encryptedData as BufferSource
    );

    const plaintext = decoder.decode(decryptedData);
    const backupData = JSON.parse(plaintext) as KeyshareBackupData;

    // Verify checksum if present
    if (encryptedBackup.checksum) {
      const expected = `${backupData.userId}:${backupData.sessionId}:${encryptedBackup.encryptionMethod || 'password'}`;
      const expectedBuf = await crypto.subtle.digest('SHA-256', encoder.encode(expected));
      const expectedChecksum = bytesToBase64(new Uint8Array(expectedBuf));

      if (expectedChecksum !== encryptedBackup.checksum) {
        throw new Error('Backup integrity check failed - file may be corrupted or tampered with');
      }
    }

    console.log('[iframe][decryptKeyshare] File backup decrypted successfully');
    return backupData;
  } catch (error: any) {
    const message = error?.message || String(error);
    const name = error?.name || 'Error';

    if (name === 'OperationError' || message.includes('decrypt')) {
      throw new Error('Incorrect password - unable to decrypt backup file');
    }
    if (error instanceof SyntaxError || message.includes('JSON')) {
      throw new Error('Corrupted backup file - decryption succeeded but data is invalid');
    }

    throw new Error(`Failed to decrypt backup: ${message}`);
  }
}

// ---------------------------------------------------------------------------
// Server-managed (Basic) backups
//
// Basic: the keyshare is encrypted with a random DEK, and TSS wraps the DEK with its recovery root (Vault Transit):
// the envelope keeps the `wrapped_dek` TSS returned, and a restore asks TSS to unwrap it.
// Upgrading to Advanced encrypts the keyshare again under a new DEK wrapped with a user-held KEK
// (password or passkey): the Basic DEK never protects an Advanced backup.
// ---------------------------------------------------------------------------

/** kdf of a server-managed envelope, as returned by TSS with the wrapped DEK */
export interface ServerManagedKdf {
  prf: 'server-managed';
  hkdf: string;
  info: string;
}

/** Wraps a DEK with the TSS recovery root */
export type DekWrapper = (dek: Uint8Array) => Promise<{ wrappedDek: string; kdf: ServerManagedKdf }>;

/** How the DEK is unlocked */
export type DekUnlock =
  | { kind: 'kek'; kek: Uint8Array | ArrayBuffer }
  | { kind: 'password'; password: string };

/** User-held protection of an Advanced backup */
export type UserProtection =
  | { method: 'password'; password: string }
  | { method: 'passkey'; kek: Uint8Array | ArrayBuffer; credentialId: string };

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

async function importAesKey(raw: Uint8Array | ArrayBuffer, usage: KeyUsage[]): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, usage);
}

/** AES-GCM encrypt, returns iv (12 bytes) || ciphertext */
async function sealWith(key: CryptoKey, plaintext: Uint8Array, aadBytes: BufferSource): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aadBytes }, key, plaintext as BufferSource);
  return concatBytes(iv, new Uint8Array(ct));
}

function aadCandidatesOf(envelope: Pick<EnvelopeEncryptedShare, 'aad'>): (BufferSource | undefined)[] {
  return envelope.aad?.version >= 2
    ? [serializeAad(envelope.aad), serializeAadWithKeyOrder(envelope.aad, LEGACY_V2_KEY_ORDER)]
    : [undefined];
}

/**
 * Unwrap the DEK of an envelope. Returns the AAD that matched so the share is decrypted (and a new
 * wrap is sealed) with the same one.
 */
export async function unwrapDek(
  envelope: Pick<EnvelopeEncryptedShare, 'wrapped_dek' | 'aad'>,
  unlock: DekUnlock
): Promise<{ dek: Uint8Array; aadBytes: BufferSource | undefined }> {
  const wrapped = base64ToBytes(envelope.wrapped_dek);

  let key: CryptoKey;
  let iv: Uint8Array;
  let ct: Uint8Array;
  if (unlock.kind === 'password') {
    const salt = wrapped.slice(0, 16);
    iv = wrapped.slice(16, 28);
    ct = wrapped.slice(28);
    key = await deriveKeyFromPassword(unlock.password, salt);
  } else {
    iv = wrapped.slice(0, 12);
    ct = wrapped.slice(12);
    key = await importAesKey(unlock.kek, ['decrypt']);
  }

  let lastError: unknown;
  for (const aadBytes of aadCandidatesOf(envelope)) {
    try {
      const dek = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv as BufferSource, ...(aadBytes && { additionalData: aadBytes }) },
        key,
        ct as BufferSource
      );
      return { dek: new Uint8Array(dek), aadBytes };
    } catch (error) {
      lastError = error;
    }
  }
  throw new Error('Failed to unwrap backup key: ' + (lastError instanceof Error ? lastError.message : String(lastError)));
}

/** Decrypt the share ciphertext with an already unwrapped DEK and the AAD it was sealed with */
export async function decryptShareWithDek(
  envelope: Pick<EnvelopeEncryptedShare, 'ciphertext_share'>,
  dek: Uint8Array,
  aadBytes: BufferSource | undefined
): Promise<KeyshareBackupData> {
  const data = base64ToBytes(envelope.ciphertext_share);
  const key = await importAesKey(dek, ['decrypt']);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: data.slice(0, 12) as BufferSource, ...(aadBytes && { additionalData: aadBytes }) },
    key,
    data.slice(12) as BufferSource
  );
  return JSON.parse(new TextDecoder().decode(plaintext)) as KeyshareBackupData;
}

/**
 * Encrypt the keyshare for a Basic backup: a random DEK, wrapped by TSS (`wrap`).
 * Returns the DEK too, so the caller can check the envelope before it is written, then wipe it.
 */
export async function envelopeEncryptKeyshareBasic(
  data: KeyshareBackupData,
  wrap: DekWrapper
): Promise<{ envelope: EnvelopeEncryptedShare; dek: Uint8Array }> {
  const aad = { userId: data.userId, version: 2, deviceId: 'embarkai-ui-kit' };
  const aadBytes = serializeAad(aad);

  const dek = crypto.getRandomValues(new Uint8Array(32));
  const ciphertext = await sealWith(await importAesKey(dek, ['encrypt']), new TextEncoder().encode(JSON.stringify(data)), aadBytes);
  let wrapped: Awaited<ReturnType<DekWrapper>>;
  try {
    wrapped = await wrap(dek);
  } catch (error) {
    dek.fill(0);
    throw error;
  }

  return {
    envelope: {
      ciphertext_share: bytesToBase64(ciphertext),
      wrapped_dek: wrapped.wrappedDek,
      kdf: { prf: wrapped.kdf.prf, hkdf: wrapped.kdf.hkdf, info: wrapped.kdf.info },
      alg: { data: 'aes-256-gcm', wrap: wrapped.kdf.hkdf },
      aad,
      createdAt: new Date().toISOString(),
      expiresAt: null,
      encryptionMethod: 'server-managed'
    },
    dek
  };
}

/** Decrypt a Basic backup with the DEK TSS unwrapped */
export async function envelopeDecryptKeyshareWithDek(envelope: EnvelopeEncryptedShare, dek: Uint8Array): Promise<KeyshareBackupData> {
  let lastError: unknown;
  for (const aadBytes of aadCandidatesOf(envelope)) {
    try {
      return await decryptShareWithDek(envelope, dek, aadBytes);
    } catch (error) {
      lastError = error;
    }
  }
  throw new Error('Failed to decrypt backup: ' + (lastError instanceof Error ? lastError.message : String(lastError)));
}

/**
 * Passkey envelope from an already derived passkey KEK: same layout as `envelopeEncryptKeyshare`,
 * without asking for the passkey again.
 */
export async function envelopeEncryptKeyshareWithPasskeyKek(
  data: KeyshareBackupData,
  kek: Uint8Array | ArrayBuffer,
  credentialId: string
): Promise<EnvelopeEncryptedShare> {
  const aad = { userId: data.userId, version: 2, deviceId: 'embarkai-ui-kit' };
  const aadBytes = serializeAad(aad);

  const dek = crypto.getRandomValues(new Uint8Array(32));
  const ciphertext = await sealWith(await importAesKey(dek, ['encrypt']), new TextEncoder().encode(JSON.stringify(data)), aadBytes);
  const wrappedDek = await sealWith(await importAesKey(kek, ['encrypt']), dek, aadBytes);

  return {
    ciphertext_share: bytesToBase64(ciphertext),
    wrapped_dek: bytesToBase64(wrappedDek),
    kdf: { prf: 'webauthn-prf', hkdf: 'hkdf-sha256', info: 'client-share' },
    alg: { data: 'aes-256-gcm', wrap: 'aes-256-gcm' },
    aad,
    createdAt: new Date().toISOString(),
    expiresAt: null,
    encryptionMethod: 'passkey',
    credentialId: credentialId || undefined
  };
}

/** Decrypt an envelope whose DEK is wrapped locally with `kek` (a passkey-derived KEK) */
export async function envelopeDecryptKeyshareWithKek(
  envelope: EnvelopeEncryptedShare,
  kek: Uint8Array | ArrayBuffer
): Promise<KeyshareBackupData> {
  const { dek, aadBytes } = await unwrapDek(envelope, { kind: 'kek', kek });
  return decryptShareWithDek(envelope, dek, aadBytes);
}
